/**
 * wineengine variant — UI overrides + extras for tenant `wineengine`.
 *
 * Reference: /RedConcierge/RedConcierge/static/javascript/main.js (The Wine Engine).
 *
 * GUIDED FLOW (old addWelcomeMessageAndButtons):
 *   1. Take The Wine Quiz     → "Take the wine quiz"
 *   2. Shop Our Wines         → "Show me all available wines" (+ pagination)
 *   3. Build Wine Box         → "Build a wine box"
 *   4. Check Out Our Offers   → "Show me your latest offers" (when is_event)
 *   5. Contact Our Team       → __contact__
 *
 * REGISTERED:
 *   - WineCard  — Add to Basket / discount / meta (createWineListing parity)
 *   - MembershipCard — Build Now → build-box mode + wine listing
 *   - WineBoxDrawer — slots / tiers / monthly|one-off (before-input)
 *   - BuildBoxCart — Your Cart after Continue (monthly / one-off)
 *   - CartActions — "View Basket" under wine listings
 *   - cart + checkout features when shopify / show_cart
 *   - account feature (Fogarty-style Login / Register on first load)
 *   - contact-options / live-person-form
 */

import { on, addMessage, getState, setState } from "../../core/state.js";
import {
  getContact,
  getFeature,
  getUiFeature,
  getTheme,
} from "../../core/configAccessors.js";
import { registerMessageKind } from "../../components/base/messages/index.js";
import { registry } from "../../core/registry.js";
import ContactOptions from "../../components/base/messages/ContactOptions.js";
import LivePersonForm from "../../components/base/messages/LivePersonForm.js";
import * as cartFeature from "../../features/optional/cart.js";
import * as checkoutFeature from "../../features/optional/checkout.js";
import * as accountFeature from "../../features/optional/account.js";
import WineEngineWineCard from "./components/WineCard.js";
import WineEngineMembershipCard from "./components/MembershipCard.js";
import WineEngineCartActions from "./components/CartActions.js";
import WineEngineFooter from "./components/Footer.js";
import WineBoxDrawer from "./components/WineBoxDrawer.js";
import BuildBoxCart from "./components/messages/BuildBoxCart.js";
import QuizControls from "./components/QuizControls.js";
import QuizQuestion from "./components/messages/QuizQuestion.js";
import QuizResults from "./components/messages/QuizResults.js";
import QuizEnded from "./components/messages/QuizEnded.js";
import * as wineQuizFeature from "./features/wine-quiz.js";
import * as wineBoxFeature from "./features/wine-box.js";

const GUIDED_FLOW = [
  {
    label: "Take The Wine Quiz",
    value: "Take the wine quiz",
    icon: "bolt",
    quizStatus: "start_quiz",
    suppressUserBubble: true,
  },
  { label: "Shop Our Wines", value: "Show me all available wines", icon: "bolt" },
  { label: "Build Wine Box", value: "Build a wine box", icon: "bolt" },
  {
    label: "Check Out Our Offers",
    value: "Show me your latest offers",
    icon: "bolt",
    visible_when: { is_event: true },
  },
  { label: "Contact Our Team", value: "__contact__", icon: "bolt" },
];

export function getGuidedFlow() {
  const { isQR, openagent } = getState().session || {};
  const showQuiz = !isQR || openagent;
  return GUIDED_FLOW.filter(
    (item) => showQuiz || item.value !== "Take the wine quiz",
  );
}

function postDefaultOptions() {
  addMessage({
    role: "bot",
    kind: "text",
    data: { text: "What would you like to do next?" },
  });
  addMessage({ role: "bot", kind: "prompts", data: { items: getGuidedFlow() } });
}

function telHref(phone) {
  return `tel:${String(phone || "").replace(/[^+0-9]/g, "")}`;
}

export async function register({ config, shadow }) {
  registerMessageKind("contact-options", ContactOptions);
  registerMessageKind("live-person-form", LivePersonForm);
  registerMessageKind("cart-actions", WineEngineCartActions);
  registerMessageKind("build-box-cart", BuildBoxCart);
  registerMessageKind("quiz-question", QuizQuestion);
  registerMessageKind("quiz-results", QuizResults);
  registerMessageKind("quiz-ended", QuizEnded);

  registry.registerComponent("WineCard", WineEngineWineCard);
  registry.registerComponent("MembershipCard", WineEngineMembershipCard);
  registry.registerComponent("Footer", WineEngineFooter);
  registry.registerSlot("before-input", WineBoxDrawer);
  registry.registerSlot("before-footer", QuizControls);

  wineQuizFeature.init({ shadow });
  wineBoxFeature.init({ shadow });

  // Shopify cart — same gate as Fogarty so Add to Basket / View Basket work.
  // Also init when only show_cart is on (wineengine admin often enables cart
  // before the shopify flag is flipped in every env).
  if (getUiFeature("show_cart", false) || getFeature("shopify")) {
    cartFeature.init();
    checkoutFeature.init();
  }

  // Logged-out Login / Register greeting on first load (same as Fogarty
  // greetLoggedOut). Opens theme.account_login_url / account_register_url.
  // Enable when the UI flag is on OR the login URL is already in tenant theme
  // (wineengine admin stores both URLs on theme today).
  if (
    getUiFeature("show_user_account", false) ||
    getTheme("account_login_url", null)
  ) {
    accountFeature.init();
  }

  // Always use variant guided flow so quiz chips carry quizStatus=start_quiz
  // (tenant-config JSON often omits those fields).
  // Promo strip — old .promotion-text layout (ss1): caption + CTA each on 2 lines.
  const cfg = getState().tenant || {};
  const theme = { ...(cfg.theme || {}) };
  theme.banner_text =
    "Explore wines and club options tailored to your preferences.";
  theme.banner_cta_label = "TAKE THE\nQUIZ NOW!";
  if (!theme.banner_cta_query) {
    theme.banner_cta_query = "Take the wine quiz";
  }
  // Keep mauve promotion.png undimmed so the white CTA pill reads like old UI.
  theme.banner_promo_dim = false;
  setState({
    tenant: { ...cfg, theme, guided_flow_questions: getGuidedFlow() },
  });

  on("ws-prompt-select", ({ value }) => {
    if (value === "__contact__") {
      addMessage({
        role: "bot",
        kind: "text",
        data: { text: "How would you like to connect with our team?" },
      });
      addMessage({
        role: "bot",
        kind: "contact-options",
        data: { email: getContact("email"), phone: getContact("phone") },
      });
    }
  });

  on("ws-contact-text", () => {
    addMessage({
      role: "bot",
      kind: "text",
      data: {
        text: "Enter your information and we will text you between the hours of 9AM to 5PM.",
      },
    });
    const brand = (getState().tenant || {}).display_name || "The Wine Engine";
    addMessage({ role: "bot", kind: "live-person-form", data: { brand } });
  });

  on("ws-contact-submit", () => {
    addMessage({
      role: "bot",
      kind: "text",
      data: {
        text: "We've received your information and we will text you between the hours of 9AM to 5PM.",
      },
    });
    postDefaultOptions();
  });

  on("ws-contact-call", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: {
        text: `You can call us at [${phone}](${telHref(phone)}). Our team is available during business hours.`,
      },
    });
    postDefaultOptions();
  });

  on("ws-contact-email", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: {
        text: `We'll get back to you within 24–48 hours. For faster assistance, please call us at [${phone}](${telHref(phone)}).`,
      },
    });
    postDefaultOptions();
  });
}
